/**
 * TAILWIND BADGE KOMPONENTE
 *
 * Status-Badge basierend auf Tailwind Plus "Flat" Variante.
 * Für Status-Anzeigen, Labels, Tags etc.
 *
 * Props:
 *   children    - ReactNode, Badge-Inhalt (Text)
 *   color?      - Farbvariante (default: 'gray')
 *   size?       - 'sm' | 'md' (default: 'md')
 *   pill?       - boolean, runde Form statt rounded-md (default: false)
 *   icon?       - ReactNode, Icon vor dem Text
 *   className?  - zusätzliche Klassen
 *
 * Usage:
 *   <TWBadge color="green">Bestätigt</TWBadge>
 *   <TWBadge color="red" pill>Storniert</TWBadge>
 *   <TWBadge color="yellow" icon={<Clock className="w-3 h-3" />}>Ausstehend</TWBadge>
 */

import clsx from 'clsx';

const colorMap = {
  gray:    'bg-gray-400/10 text-gray-400',
  red:     'bg-red-400/10 text-red-400',
  yellow:  'bg-yellow-400/10 text-yellow-500',
  amber:   'bg-amber-400/10 text-amber-500',
  green:   'bg-green-400/10 text-green-400',
  blue:    'bg-blue-400/10 text-blue-400',
  indigo:  'bg-indigo-400/10 text-indigo-400',
  purple:  'bg-purple-400/10 text-purple-400',
  pink:    'bg-pink-400/10 text-pink-400',
  teal:    'bg-teal-400/10 text-teal-400',
  emerald: 'bg-emerald-400/10 text-emerald-400',
  orange:  'bg-orange-400/10 text-orange-400',
  brand:   'bg-brand/10 text-brand',
} as const;

export type TWBadgeColor = keyof typeof colorMap;

interface TWBadgeProps {
  children: React.ReactNode;
  color?: TWBadgeColor;
  size?: 'sm' | 'md';
  pill?: boolean;
  icon?: React.ReactNode;
  className?: string;
}

export const TWBadge: React.FC<TWBadgeProps> = ({
  children,
  color = 'gray',
  size = 'md',
  pill = false,
  icon,
  className,
}) => {
  return (
    <span
      className={clsx(
        'inline-flex items-center font-medium',
        colorMap[color],
        pill ? 'rounded-full' : 'rounded-md',
        size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-1 text-xs',
        icon && 'gap-1.5',
        className
      )}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      {children}
    </span>
  );
};
